import React, { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import Select from "react-select";
import CustomCalendar from "./Custom_Calendar";
import { ReactComponent as CalenderIcon } from "../../assets/icons/svg/calendarr-icon.svg";
import { ReactComponent as InformationCircle } from "../../assets/icons/svg/information-circle-icon.svg";

// Define the interface for the duration option
interface DurationOption {
  value: string;
  label: string;
}

// Define the interface for the component props
interface LaterScheduleData {
  startDate: Date;
  endDate: Date;
  duration: DurationOption | null;
}

interface LaterSelectorCardProps {
  initialData?: LaterScheduleData;
  onDataChange: (data: LaterScheduleData) => void;
  durationOptions?: DurationOption[]; // Prop for custom duration options
}

// Styled dropdown
const customSelectStyles = {
  control: (provided: any) => ({
    ...provided,
    borderRadius: 8,
    borderColor: "#d1d5db",
    padding: 2,
    boxShadow: "none",
    "&:hover": { borderColor: "#a1a1aa" },
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: 8,
    backgroundColor: "#ffffff",
    padding: 10,
    boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.15)",
    width: "95%",
    margin: 6,
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: 0,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    borderRadius: 8,
    backgroundColor: state.isSelected ? "#eff6ff" : "#ffffff", // Only highlight selected option
    color: "#1f2937",
    padding: 12,
    marginBottom: 8,
    cursor: "pointer",
    transition: "background-color 0.2s",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "#1f2937",
  }),
};

const LaterSelectorCard: React.FC<LaterSelectorCardProps> = ({
  initialData,
  onDataChange,
  durationOptions = [
    { value: "10 sec", label: "10 seconds" },
    { value: "20 sec", label: "20 seconds" },
    { value: "30 sec", label: "30 seconds" },
  ],
}) => {
  const [startDate, setStartDate] = useState<Date>(
    initialData?.startDate || new Date()
  );
  const [endDate, setEndDate] = useState<Date>(
    initialData?.endDate || new Date()
  );
  const [duration, setDuration] = useState<DurationOption | null>(
    initialData?.duration || null
  );
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [isDurationMenuOpen, setIsDurationMenuOpen] = useState(false);
  const hasInitialized = useRef(false);

  // Initialize state from initialData when component mounts
  useEffect(() => {
    if (initialData && !hasInitialized.current) {
      setStartDate(initialData.startDate || new Date());
      setEndDate(initialData.endDate || new Date());
      setDuration(initialData.duration || null);

      // Validate dates on initialization
      if (
        initialData.endDate &&
        initialData.startDate &&
        initialData.endDate.getTime() < initialData.startDate.getTime()
      ) {
        setEndDateError("End date must be on or after the start date.");
      }

      hasInitialized.current = true;
    }
  }, [initialData]);

  // Update parent when data changes and is valid (including initial setup)
  useEffect(() => {
    if (startDate && endDate && !endDateError) {
      const data: LaterScheduleData = {
        startDate,
        endDate,
        duration,
      };
      onDataChange(data);
    }
  }, [startDate, endDate, duration, endDateError, onDataChange]);

  const handleStartDateClick = () => {
    setShowStartCalendar(!showStartCalendar);
    setShowEndCalendar(false); // Close end date calendar
    setIsDurationMenuOpen(false); // Close duration dropdown
  };

  const handleEndDateClick = () => {
    setShowEndCalendar(!showEndCalendar);
    setShowStartCalendar(false); // Close start date calendar
    setIsDurationMenuOpen(false); // Close duration dropdown
  };

  const handleStartDateChange = (date: Date) => {
    setStartDate(date);
    setShowStartCalendar(false);
    // Validate endDate against new startDate
    if (endDate && date.getTime() > endDate.getTime()) {
      setEndDateError("End date must be on or after the start date.");
    } else {
      setEndDateError(null);
    }
  };

  const handleEndDateChange = (date: Date) => {
    if (date.getTime() >= startDate.getTime()) {
      setEndDate(date);
      setEndDateError(null);
    } else {
      setEndDateError("End date must be on or after the start date.");
    }
    setShowEndCalendar(false);
  };

  const handleDurationChange = (selected: DurationOption | null) => {
    setDuration(selected);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Info banner */}
      <div className="text-xs 2xl:text-sm text-[#0BA6C7] mb-2 w-full">
        <div className="flex items-center bg-blue-50 px-4 py-2 rounded-lg w-full">
          <InformationCircle className="w-4 h-4 mr-2 text-blue-600" />
          <span>Start and end date is mandatory.</span>
        </div>
      </div>

      {/* Start Date */}
      <div className="relative">
        <label className="block text-sm font-semibold text-gray-700 mb-1">
          Start date<span className="text-red-500">*</span>:
        </label>
        <div className="relative">
          <input
            readOnly
            value={format(startDate, "dd/MM/yyyy")}
            onClick={handleStartDateClick}
            className="w-full border border-gray-300 rounded px-3 py-2 cursor-pointer"
          />
          <span className="absolute right-3 top-2.5 pointer-events-none">
            <CalenderIcon width={24} height={24} />
          </span>
        </div>
        {showStartCalendar && (
          <div className="absolute z-10 mt-2">
            <CustomCalendar
              selectedDate={startDate}
              onDateChange={handleStartDateChange}
            />
          </div>
        )}
      </div>

      {/* End Date */}
      <div className="relative">
        <label className="block text-sm font-semibold text-gray-700 mb-1">
          End date<span className="text-red-500">*</span>:
        </label>
        <div className="relative">
          <input
            readOnly
            value={format(endDate, "dd/MM/yyyy")}
            onClick={handleEndDateClick}
            className={`w-full border rounded px-3 py-2 cursor-pointer ${
              endDateError ? "border-red-500" : "border-gray-300"
            }`}
          />
          <span className="absolute right-3 top-2.5 pointer-events-none">
            <CalenderIcon width={24} height={24} />
          </span>
        </div>
        {showEndCalendar && (
          <div className="absolute z-10 mt-2">
            <CustomCalendar
              selectedDate={endDate}
              onDateChange={handleEndDateChange}
              minDate={startDate}
            />
          </div>
        )}
        {endDateError && (
          <p className="text-red-500 text-xs mt-1">{endDateError}</p>
        )}
      </div>

      {/* Duration Dropdown */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">
          Duration<span className="text-red-500">*</span>:
        </label>
        <Select
          options={durationOptions}
          value={duration}
          onChange={handleDurationChange}
          styles={customSelectStyles}
          placeholder="Select Duration"
          isClearable
          menuIsOpen={isDurationMenuOpen}
          onMenuOpen={() => {
            setIsDurationMenuOpen(true);
            setShowStartCalendar(false); // Close start date calendar
            setShowEndCalendar(false); // Close end date calendar
          }}
          onMenuClose={() => setIsDurationMenuOpen(false)}
        />
      </div>
    </div>
  );
};

export default LaterSelectorCard;
